<template>
  <div>
    <!-- Job Modal -->
    <v-navigation-drawer
      v-model="showModal"
      right
      temporary
      :width="modalWidth"
      height="100vh"
      class="job-modal"
      overlay-opacity="0.3"
      :stateless="childModalOpen"
      :hide-overlay="childModalOpen"
      touchless
      fixed
    >
      <!-- Loading bar while job data initializes -->
      <div v-if="loadingJob" class="px-4 pt-2">
        <v-progress-linear color="primary" indeterminate rounded height="6"></v-progress-linear>
      </div>

      <!-- Deliverables panel inside the same drawer -->
      <template v-else-if="['deliverables', 'deliverableAssetsList', 'deliverableAssets'].includes(activePanel)">
        <div class="d-flex flex-column" style="height: 100vh;">
          <v-toolbar dense flat class="px-2 py-0 deliverables-toolbar" style="max-height: 48px !important;">
            <v-btn icon @click.prevent="backToForm"><v-icon>mdi-arrow-left</v-icon></v-btn>
            <div class="d-flex align-center mr-3">
              <span class="subtitle-2 font-weight-medium mr-1">{{ $t('deliverables') }}</span>
              <v-icon class="mr-1">mdi-chevron-right</v-icon>
              <span class="subtitle-2 grey--text text--darken-1">{{ jobData.job_title }}</span>
            </div>
            <v-spacer></v-spacer>
            <v-btn icon @click.stop="toggleMaximize"><v-icon>{{ isMaximized ? 'mdi-arrow-collapse' : 'mdi-arrow-expand' }}</v-icon></v-btn>
            <v-btn icon @click.stop="backToForm"><v-icon>mdi-close</v-icon></v-btn>
          </v-toolbar>

          <div class="deliverables-content flex-grow-1 overflow-y-auto">
            <JobDeliverableManagement
              v-if="activePanel === 'deliverables'"
              ref="deliverableManagement"
              :job-id="jobData.job_id || jobId"
              :job-data="jobData"
              :customer-id="jobData.customer_id"
              :embedded="true"
              :readonly="currentMode === 'consult' || isJobReadonly"
              :shared-deliverables.sync="sharedDeliverables"
              @deliverables-changed="onDeliverablesChanged"
              @open-deliverable-assets-list="openDeliverableAssetsListPanel"
              @open-deliverable-assets="openDeliverableAssetsPanel"
            />
            <DeliverableAssetsListManagement
              v-else-if="activePanel === 'deliverableAssetsList'"
              :job-id="jobData.job_id || jobId"
              :job-data="jobData"
              :customer-id="jobData.customer_id"
              :embedded="true"
              @open-deliverable-management="openDeliverablesPanel"
              @open-deliverable-assets="openDeliverableAssetsPanel"
            />
            <DeliverableAssetsManagement
              v-else
              :job-id="jobData.job_id || jobId"
              :job-data="jobData"
              :customer-id="jobData.customer_id"
              :embedded="true"
              @open-deliverable-management="openDeliverablesPanel"
              @open-deliverable-assets-list="openDeliverableAssetsListPanel"
            />
          </div>
        </div>
      </template>

      <!-- Files panel inside the same drawer -->
      <template v-else-if="activePanel === 'files'">
        <div class="d-flex flex-column" style="height: 100vh;">
          <v-toolbar dense flat class="px-2 py-0 files-toolbar" style="max-height: 48px !important;">
            <v-btn icon @click.prevent="backToForm"><v-icon>mdi-arrow-left</v-icon></v-btn>
            <div class="d-flex align-center mr-3">
              <span class="subtitle-2 font-weight-medium mr-1">{{ $t(currentFileType === 1 ? 'references' : 'finals') }}</span>
              <v-icon class="mr-1">mdi-chevron-right</v-icon>
              <span class="subtitle-2 grey--text text--darken-1">{{ jobData.job_title }}</span>
            </div>
            <v-spacer></v-spacer>
            <v-btn icon @click.stop="toggleMaximize"><v-icon>{{ isMaximized ? 'mdi-arrow-collapse' : 'mdi-arrow-expand' }}</v-icon></v-btn>
            <v-btn icon @click.stop="backToForm"><v-icon>mdi-close</v-icon></v-btn>
          </v-toolbar>
          <span class="px-4 instruction-text">{{ $t('send_files_or_reference_on_dam') }}</span>
          <div class="refs-actions px-4 pt-1 pb-1">
            <v-btn medium outlined color="primary" class="mr-2" :disabled="isFileFieldReadonly" @click.stop="openUploadModal(currentFileType)">
              <v-icon left medium>mdi-upload</v-icon>{{ $t('upload') }}
            </v-btn>
            <v-btn medium outlined color="primary" class="mr-2" :disabled="isFileFieldReadonly" @click.stop="openLinkModal(currentFileType)">
              <v-icon left medium>mdi-database-search</v-icon>{{ $t('select_from_dam') }}
            </v-btn>
            <v-btn
              medium
              outlined
              color="primary"
              :disabled="!selectedFileNodeIds.length || fileDownloadInProgress"
              @click.stop="downloadSelectedFiles"
            >
              <v-icon left medium>mdi-download-multiple</v-icon>
              {{ $t('download') }}{{ selectedFileNodeIds.length ? ` (${selectedFileNodeIds.length})` : '' }}
            </v-btn>
          </div>

          <!-- Pending placeholders (uploads/links queued until Save) -->
          <div v-if="hasPendingForCurrent" class="px-4 pb-1">
            <v-card outlined elevation="0">
              <v-card-title class="py-2">
                <span class="subtitle-2">Pendentes (processados ao salvar)</span>
              </v-card-title>
              <v-card-text class="pt-1">
                <div v-if="pendingUploadsForCurrent.length" class="mb-1">
                  <div class="caption mb-1">Uploads pendentes:</div>
                  <div>
                    <v-chip
                      v-for="(f, i) in pendingUploadsForCurrent"
                      :key="'up-'+i+'-'+(f.name || i)"
                      small
                      class="mr-1 mb-1 pending-chip"
                      color="primary"
                      outlined
                      close
                      @click:close="removePendingUpload(currentFileType, i)"
                    >
                      <v-icon left small>mdi-upload</v-icon>{{ f.name || $t('file') }}
                    </v-chip>
                  </div>
                </div>
                <div v-if="pendingLinksForCurrent.length">
                  <div class="caption mb-1">Vínculos pendentes:</div>
                  <div>
                    <v-chip
                      v-for="(n, i) in pendingLinksForCurrent"
                      :key="'lnk-'+i+'-'+(n.node_id || i)"
                      small
                      class="mr-1 mb-1 pending-chip"
                      color="primary"
                      outlined
                      close
                      @click:close="removePendingLink(currentFileType, i)"
                    >
                      <v-icon left small>mdi-link</v-icon>{{ (n && (n.name || n.entry?.name)) || $t('item') }}
                    </v-chip>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </div>

          <div class="refs-content flex-grow-1 overflow-y-auto px-4 pb-6 pt-3">
            <div v-if="!jobData?.job_id" class="refs-empty text-center grey--text text--darken-1 mt-10">
              {{ $t('save_job_to_add_references') }}
            </div>

            <div v-else-if="loadingReferences" class="file-placeholder">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>

            <template v-else>
              <v-row v-if="references.length > 0" class="mt-1">
                <!-- Adjust grid columns for drawer width to avoid narrow cards -->
                <v-col v-for="item in references" :key="item._key" xl="3" lg="4" md="6" sm="12" cols="12" class="py-2 px-2 d-flex child-flex">
                  <div class="file-card-wrapper">
                    <FileCard
                      ref="jobFileCards"
                      :data="item.entry"
                      disable-actions-menu
                      class="w-100"
                      @toggle-selection="toggleFileSelection"
                    />
                    <div class="file-actions-bottom-right">
                      <v-menu offset-y :attach="true">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn icon v-bind="attrs" v-on="on">
                            <v-icon>mdi-chevron-down</v-icon>
                          </v-btn>
                        </template>
                        <v-card class="card" outlined>
                          <v-list dense>
                            <v-list-item @click="download(item.entry)">
                              <v-list-item-icon class="mr-2"><v-icon color="defaultIconColor">mdi-download</v-icon></v-list-item-icon>
                              <v-list-item-title>{{ $t('download') }}</v-list-item-title>
                            </v-list-item>
                            <v-list-item v-if="! isFileFieldReadonly" @click="confirmUnlink(item)">
                              <v-list-item-icon class="mr-2"><v-icon color="defaultIconColor">mdi-link-off</v-icon></v-list-item-icon>
                              <v-list-item-title>{{ $t('unlink') }}</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-card>
                      </v-menu>
                    </div>
                  </div>
                </v-col>
              </v-row>

              <div v-else class="refs-empty text-center grey--text text--darken-1 mt-10">
                {{ $t('no_files_found_send_or_select') }}
              </div>
            </template>
          </div>
        </div>
      </template>

      <div v-else class="job-modal-content d-flex flex-row" style="height: 100vh;">
        <!-- Job Description Section (Left side) -->
        <div class="job-description-section">
          <!-- Header -->
          <div class="d-flex align-center justify-space-between pa-4">
            <h3 class="job-title">
              {{ $t('job_description') }}
            </h3>
            <div class="header-actions">
              <v-btn
                icon
                @click="toggleMaximize"
                :title="isMaximized ? $t('restore') : $t('maximize')"
              >
                <v-icon>{{ isMaximized ? 'mdi-arrow-collapse' : 'mdi-arrow-expand' }}</v-icon>
              </v-btn>
              <v-btn
                icon
                @click="closeModal"
                :title="$t('close')"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>
          </div>

          <!-- Form Content -->
          <div class="job-form-content pa-4 flex-grow-1 overflow-y-auto">
            <v-form ref="jobForm" v-model="formValid">

              <v-row class="form-row" style="padding-top: 0px">
                <v-col cols="6" style="padding-top: 0px">
                  <v-text-field
                    v-model="jobData.job_title"
                    :label="`${$t('title')}`"
                    outlined
                    dense
                    v-bind="jobTitleProps"
                  ></v-text-field>
                </v-col>

                <v-col cols="4" style="padding-top: 0px">
                  <v-text-field
                    v-model="jobData.job_customer_code"
                    :label="$t('job_customer_code')"
                    outlined
                    dense
                    v-bind="jobCustomerCodeProps"
                  ></v-text-field>
                </v-col>

                <v-col cols="2" style="padding-top: 0px">
                  <v-text-field
                    :value="jobData.job_id || ''"
                    :label="$t('jamworks_job_id')"
                    outlined
                    dense
                    v-bind="jobIdProps"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row class="form-row" style="margin-top: 0px;">
                <v-col cols="4" style="padding-top: 0px">
                  <v-select
                    v-model="jobData.customer_id"
                    :items="customerOptions"
                    :label="`${$t('customer')}`"
                    item-text="customer_name"
                    item-value="customer_id"
                    outlined
                    dense
                    v-bind="jobCustomerProps"
                  ></v-select>
                </v-col>

                <v-col cols="4" style="padding-top: 0px">
                  <v-select
                    v-model="jobData.campaign_id"
                    :items="campaignOptions"
                    :label="`${$t('campaign')}`"
                    item-text="campaign_title"
                    item-value="campaign_id"
                    outlined
                    dense
                    :disabled="!jobData.customer_id"
                    v-bind="jobCampaignProps"
                  ></v-select>
                </v-col>

                <v-col cols="4" style="padding-top: 0px">
                  <v-menu
                    ref="datePickerMenu"
                    v-model="datePickerMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    min-width="auto"
                    offset-y
                    @input="onDatePickerMenuChange"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="formattedDeadline"
                        :label="$t('deadline')"
                        outlined
                        dense
                        v-bind="{...jobDeadlineProps, ...attrs}"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="tempDeadline"
                      :locale="currentLocale"
                      :readonly="currentMode === 'consult' || isJobReadonly"
                      no-title
                      scrollable
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        text
                        color="primary"
                        @click="cancelDeadline"
                      >
                        {{ $t('cancel') }}
                      </v-btn>
                      <v-btn
                        text
                        color="primary"
                        @click="saveDeadline"
                      >
                        {{ $t('ok') }}
                      </v-btn>
                    </v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>

              <v-row
                v-if="dynamicFormFields.length > 0"
                class="form-row mt-0"
              >
                <v-col class="pt-0">
                  <v-form ref="fieldsForm">
                    <DynamicFormContent
                      @openFilesPanel="openFilesPanel"
                      @openDeliverablesPanel="openDeliverablesPanel"
                      :fields="dynamicFormFields"
                      :entity="this.jobData"
                      :nodeIds="dynamicFormNodeIds"
                      :userAcls="userAcls"
                      :lanes="lanes"
                      :current-pool-id="jobDefinition && jobDefinition.pool_id"
                      :owner-pool-id="jobDefinition && jobDefinition.owner_pool_id"
                      :user-lanes="userLanes"
                      :app-config="appConfig"
                      ref="dynamicForm"
                      class="pa-0"
                    ></DynamicFormContent>
                  </v-form>
                </v-col>
              </v-row>

              <!-- Sixth Line: Status -->
              <v-row class="form-row" style="margin-top: 0px;">
                <v-col cols="12" style="padding-top: 0px">
                  <v-card outlined class="status-container">
                    <v-card-title class="py-2">
                      <span class="text-subtitle-1">{{ $t('current_status') }}</span>
                    </v-card-title>
                    <v-card-text>
                      <!-- Status Fields Row -->
                      <v-row class="mb-1">
                        <v-col :cols="currentMode === 'create' ? 6 : 4">
                          <v-select
                            v-model="jobData.status_id"
                            :items="statusOptions"
                            :rules="[v => !!v || this.$t('field_required')]"
                            :label="$t('status')"
                            item-text="title"
                            item-value="status_id"
                            outlined
                            dense
                            :readonly="currentMode === 'consult' || isJobReadonly"
                            hide-details
                          ></v-select>
                        </v-col>
                        <v-col :cols="currentMode === 'create' ? 6 : 4" v-if="currentMode === 'create'">
                          <v-select
                            v-model="jobData.lane_id"
                            :items="ownerLaneOptions"
                            :rules="[v => !!v || this.$t('field_required')]"
                            :label="$t('owner_lane')"
                            item-text="lane_title"
                            item-value="lane_id"
                            outlined
                            dense
                            required
                            hide-details
                          ></v-select>
                        </v-col>
                        <v-col cols="4" v-if="currentMode !== 'create'">
                          <v-text-field
                            v-model="startDateDisplay"
                            :label="$t('start_date')"
                            outlined
                            dense
                            readonly
                            disabled
                            type="text"
                            inputmode="numeric"
                            placeholder="dd/mm/aaaa"
                            prepend-inner-icon="mdi-clock-outline"
                            hide-details
                          ></v-text-field>
                        </v-col>
                        <v-col cols="4" v-if="currentMode !== 'create'">
                          <v-text-field
                            v-model="endDateDisplay"
                            :label="$t('end_date')"
                            outlined
                            dense
                            readonly
                            disabled
                            type="text"
                            inputmode="numeric"
                            placeholder="dd/mm/aaaa"
                            prepend-inner-icon="mdi-clock-outline"
                            hide-details
                          ></v-text-field>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Action Buttons -->
              <v-row class="form-row" style="margin-top: 0px;">
                <v-col cols="12" class="d-flex justify-end" style="padding-top: 0px">
                  <v-btn
                    v-if="currentMode === 'update'"
                    color="error"
                    @click="showDeleteConfirmation"
                    :loading="deleting"
                    outlined
                  >
                    <v-icon left>mdi-delete-outline</v-icon>
                    {{ isSubtask ? $t('delete_subtask') : $t('delete_job') }}
                  </v-btn>
                  <v-btn
                    @click="closeModal"
                    outlined
                    class="ml-2"
                  >
                    {{ $t('close_without_saving') }}
                  </v-btn>

                  <v-btn
                    v-if="currentMode !== 'create' && jobData.job_folder_id"
                    @click="openJobFilesFolder"
                    outlined
                    class="ml-2"
                  >
                    <v-icon left>mdi-folder-open-outline</v-icon>
                     {{ $t('files') }}
                  </v-btn>

                  <v-btn
                    v-if="currentMode !== 'consult'"
                    @click="saveJob"
                    :loading="saving"
                    :disabled="!formValid"
                    class="ml-2"
                    outlined
                  >
                    {{ currentMode === 'create' ? `${$t('create')}` : isSubtask ? `${$t('save_subtask')}` : `${$t('save_job')}` }}
                  </v-btn>

                  <v-btn
                    v-if="currentMode !== 'consult' && currentMode !== 'create' && false == isJobReadonly"
                    :loading="saving"
                    :disabled="!formValid"
                    color="primary"
                    class="ml-2"
                    @click="openMoveToLaneDialog()"
                  >
                    <v-icon left>mdi-send-variant-outline</v-icon>
                    {{ isSubtask ? $t('send_subtask') : $t('send_job') }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>
          </div>
        </div>

        <!-- Comments Section (Right side) - Collapsible -->
        <div class="comments-section-wrapper" :class="{ 'collapsed': isCommentsCollapsed }">
          <div class="comments-toggle-btn" @click="toggleCommentsPanel">
            <v-icon>{{ isCommentsCollapsed ? 'mdi-chevron-left' : 'mdi-chevron-right' }}</v-icon>
          </div>
          <div class="comments-section">
            <CommentsView
              :job-id="jobData.job_id || jobId"
              :groups="groups"
              :users="users"
              :current-mode="currentMode"
              @mention-opened="onChildModalOpen"
              @mention-closed="onChildModalClose"
              @comments-loaded="onCommentsLoaded"
            />
          </div>
        </div>
      </div>
    </v-navigation-drawer>

    <!-- Hidden selectors for linking existing nodes -->
    <SelectFileField
      :key="selectRefKey"
      v-show="false"
      ref="selectRef"
      :label="$t('references')"
      :parent-node-id="currentDamStartNodeId.references"
      files-only
      multiple-select
      selection-basket
      :root-nav-behavior="'navigate'"
      @selectNodes="(nodes) => onSelectNodesForLink(1, nodes)"
      @opened="onChildModalOpen"
      @closed="onChildModalClose"
    />

    <!-- Hidden selector for Materials -->
    <SelectFileField
      :key="selectMatKey"
      v-show="false"
      ref="selectMat"
      :label="$t('finals')"
      :parent-node-id="currentDamStartNodeId.finals"
      files-only
      multiple-select
      selection-basket
      :root-nav-behavior="'navigate'"
      @selectNodes="(nodes) => onSelectNodesForLink(2, nodes)"
      @opened="onChildModalOpen"
      @closed="onChildModalClose"
    />

    <!-- Upload Modal for References -->
    <DefaultModal
      :title="$t('file_upload')"
      :submit-button-text="'upload'"
      :saveButtonDisabled="modalSaveButtonDisabled"
      @submit="makeUpload"
      @modal-closed="onChildModalClose"
      ref="uploadModal"
      eager
      style="z-index: 10002;"
    >
      <template v-slot:content>
        <UploadField
          :rules="[validateFileSelected, validateFilename]"
          :errorMessage="uploadErrorMessage"
          @clearInput="resetUploadBehavior()"
          @change.native="setUploadBehavior($event.target.files)"
          ref="uploadFileField"
        />
      </template>
    </DefaultModal>

    <!-- Calendar Dialog -->
    <v-dialog v-model="showCalendar" max-width="400px">
      <v-card>
        <v-card-title>{{ $t('select_deadline') }}</v-card-title>
        <v-card-text>
          <v-date-picker
            v-model="selectedDate"
            @input="selectDate"
            full-width
          ></v-date-picker>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Move Job Modal (after save in edit mode) -->
    <MoveToLaneDialog
      v-model="showMoveDialog"
      :job="jobData"
      :pools="canMoveToPools"
      :statuses="statuses"
      :lanes="lanes"
      :groups="groups"
      :initial-selection="moveInitialSelection"
      @confirm="handleMoveDialogConfirm"
      @opened="onChildModalOpen"
      @closed="onChildModalClose"
      style="z-index: 10002;"
    />

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteConfirmDialog" max-width="500px" style="z-index: 10000;">
      <v-card>
        <v-card-title class="text-h6">{{ $t('confirm_deletion') }}</v-card-title>
        <v-card-text>
          <p class="body-2 black--text">{{ $t('delete_job_warning') }} {{ $t('action_cannot_be_undone') }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelDelete">{{ $t('cancel') }}</v-btn>
          <v-btn color="primary" text @click="confirmDelete" :loading="deleting">{{ $t('confirm') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Unlink File Confirmation Dialog -->
    <DefaultModal
      @submit="executeUnlink"
      :title="$t('unlink')"
      ref="modalUnlink"
      submit-button-text="unlink"
      class="job-modal"
      deleteModal
    >
      <template v-slot:content>
        {{ $t('unlink_file_confirmation') }} <b>{{ itemToUnlink?.entry?.name || '' }}</b>?
      </template>
    </DefaultModal>

    <!-- Inline floating editor handled by v-menu in-cell -->
    <MoveToLaneDialog
      @submit="saveAndMoveToLane"
      @opened="onChildModalOpen"
      @closed="onChildModalClose"
      :job="jobData"
      :pools="canMoveToPools"
      :lanes="lanes"
      :user-lanes="userLanes"
      :statuses="statuses"
      :groups="groups"
      :initial-selection="moveInitialSelection"
      ref="moveToLaneDialog"
      style="z-index: 10002;"
    />
  </div>
</template>

<script>
import { format, parseISO } from 'date-fns'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import JobAPI from '@/services/api/job/job'
import JobFileAPI from '@/services/api/job/job_file'
import CommentAPI from '@/services/api/job/comment'
import JobPoolStatusAPI from '@/services/api/job/job_pool_status'
import PoolStatusAPI from '@/services/api/job/poolstatus'
import NextStatusAPI from '@/services/api/job/next_status'
import CustomerAPI from '@/services/api/business/customer'
import CampaignAPI from '@/services/api/job/campaign'

import NotificationActions from '@/store/core/notification/actions-types'
import JobActions from '@/store/job/actions-types'
import JobMutations from '@/store/job/mutations-types'
import AppActions from '@/store/app/actions-types'
import FileCard from '@/components/content/FileCard.vue'
import SelectFileField from '@/components/content/default/Form/SelectFileField.vue'
import DefaultModal from '@/components/DefaultModal.vue'
import MoveToLaneDialog from '@/components/job/MoveToLaneDialog.vue'
import UploadField from '@/components/content/default/Form/UploadField.vue'
import ContentActions from '@/store/content/actions/actions-types'
import EntryActions from '@/store/content/entry/actions-types'
import { BgActionEventBus } from '@/bgActionEventBus'
import CommentsView from '@/components/job/CommentsView.vue'
import DynamicFormContent from '@/components/DynamicFormContent'
import JobDeliverableManagement from '@/views/job/deliverable/JobDeliverableManagement.vue'
import DeliverableAssetsListManagement from '@/components/job/DeliverableAssetsListManagement.vue'
import DeliverableAssetsManagement from '@/components/job/DeliverableAssetsManagement.vue'

export default {
  name: 'Job',

  props: {
    groups: {
      type: Array,
      default: () => []
    },
    users: {
      type: Array,
      default: () => []
    },
    statuses: {
      type: Array,
      default: () => []
    },
    pools: {
      type: Array,
      default: () => []
    },
    lanes: {
      type: Array,
      default: () => []
    },
    userLanes: {
      type: Array,
      default: () => []
    },
    campaigns: {
      type: Array,
      default: () => []
    },
    userAcls: {
      type: Array,
      default: () => []
    },
    jobDefinitions: {
      type: Array,
      default: () => []
    },
  },

  components: {
    CommentsView,
    FileCard,
    SelectFileField,
    DefaultModal,
    UploadField,
    MoveToLaneDialog,
    DynamicFormContent,
    JobDeliverableManagement,
    DeliverableAssetsListManagement,
    DeliverableAssetsManagement,
  },

  data() {
    return {
      showModal: false,
      // Controls whether local edits sync to Vuex
      suspendJobSync: false,
      // Loading bar for initial job load (create/update)
      loadingJob: false,
      isMaximized: false,
      activePanel: 'form', // 'form' | 'files'
      currentFileType: 1, // 1=references, 2=materials
      childModalOpen: false,
      showMoveDialog: false,
      // Force remount of DAM selectors to avoid stale state
      selectRefKey: 9999,
      selectMatKey: 0,
      // Dynamic damStartNodeId for references and finals
      currentDamStartNodeId: {
        references: null,
        finals: null
      },
      formValid: false,
      saving: false,
      deleting: false,
      deleteConfirmDialog: false,
      itemToUnlink: null,
      showCalendar: false,
      // Move-after-save modal form
      moveForm: {
        selectedPoolId: null,
        selectedLaneId: null,
        selectedStatusId: null,
        slaHours: null,
        sumSLA: false,
      },
      shouldOpenMoveModalAfterSave: false,
      selectedDate: null,
      tenantId: null,
      // Local mode state to avoid mutating prop
      localMode: 'create',

      datePickerMenu: false,
      tempDeadline: null,
      showMovePoolDialog: false,

      originalTitle: '',
      originalCustomerCode: '',
      originalCampaign: '',
      originalDeadline: '',
      originalStatus: '',

      jobId: null,
      jobData: {
        job_id: null,
        job_definition_id: null,
        lane_id: null,
        requester_users_id: null,
        job_title: '',
        job_customer_code: '',
        status_id: 0,
        customer_id: null,
        campaign_id: 0,
        decided_sla: 0,
        start_date: null,
        end_date: null,
        job_folder_id: 0,
        ref_folder_id: 0,
        mat_folder_id: 0,
        deadline: null,
        readonly: false,
      },

      campaignOptions: [],
      customerOptions: [],
      userUsers: [],
      userGroups: [],
      statusOptions: [],
      poolStatuses: [],
      poolAcls: [],
      laneDetail: [],
      currentJobPoolStatus: null,

      // Files (references only for now)
      loadingReferences: false,
      references: [],
      referencesType: null,
      selectedFileNodeIds: [],
      pendingFileDownloadActions: new Set(),
      fileDownloadInProgress: false,
      fileDownloadBgActionHandler: null,
      pendingUploadType: null,
      // Upload modal state
      modalSaveButtonDisabled: true,
      uploadErrorMessage: '',

      // Pending file operations (queued until Save)
      pendingUploads: { 1: [], 2: [] },
      pendingLinks: { 1: [], 2: [] },
      builtinFields: [
        'jobTitle',
        'jobId',
        'jobCampaign',
        'jobCustomer',
        'jobDeadline',
        'jobCustomerCode',
        'job_customer_code'
      ],
      jobTitleBaseProps: {
        'hide-details': true,
        rules: [v => !!v || this.$t('field_required')],
      },
      jobIdBaseProps: {
        'hide-details': true,
        disabled: true,
      },
      jobCampaignBaseProps: {
        'hide-details': true,
        rules: [v => !!v || this.$t('field_required')],
      },
      jobCustomerBaseProps: {
        'clearable': true,
        'hide-details': true,
      },
      jobCustomerCodeBaseProps: {
        'hide-details': true,
      },
      jobDeadlineBaseProps: {
        'hide-details': true,
        readonly: true,
        'prepend-icon': 'mdi-calendar',
      },
      isCommentsCollapsed: true, // Start collapsed by default
    }
  },
  computed: {
    ...mapState({
      userData: state => state.core.auth.userData,
      tempDeliverables: state => state.job.tempDeliverables,
    }),

    ...mapGetters('app', [
      'getUserApplicationById'
    ]),

    // Computed property for shared deliverables that syncs with Vuex store
    sharedDeliverables: {
      get() {
        return this.tempDeliverables || []
      },
      set(value) {
        this.$store.commit(`job/${JobMutations.SET_TEMP_DELIVERABLES}`, value)
      }
    },

    isSubtask() {
      return !!this.jobData.parent_job_id
    },

    isJobReadonly() {
      return this.jobData.readonly ?? false
    },

    jobDefinition() {
      const jobDefinitionId = this.jobData.job_definition_id ?? null

      return this.jobDefinitions.find(jd => jd.job_definition_id == jobDefinitionId)
    },

    parsedJobDefinitionForm() {
      if (!this.jobDefinition) {
        return []
      }

      let form = this.parseFormString(this.jobDefinition.form_default)

      if (this.currentJobPoolStatus) {
        const specializedForm = this.parseFormString(this.currentJobPoolStatus.form)

        if (this.isNotEmptyJson(specializedForm)) {
          form = specializedForm
        }
      }

      if (!Array.isArray(form)) {
        form = []
      }

      const hasDeliverablesField = form.some(field =>
        field?.type === 'deliverables' || field?.name === 'deliverables'
      )

      if (hasDeliverablesField) {
        return form
      }

      return [
        ...form,
        {
          label: this.$t('deliverables'),
          name: 'deliverables',
          value: null,
          type: 'deliverables',
          cols: 12,
          collapseGroup: 'Pedido'
        }
      ]
    },

    dynamicFormFields() {
      return this.parsedJobDefinitionForm
        .filter(field => !this.builtinFields.includes(field.name))
        .map(field => ({
          ...field,
          value: this.jobData?.fields?.[field.name] ?? field.value,
          readonly: (this.localMode === 'consult' || this.isJobReadonly) ? true : field.readonly,
        }))
    },

    jobTitleProps() {
      const base = this.buildFieldProps('jobTitle', this.jobTitleBaseProps)
      // Always return a new object to avoid modifying the base props
      return {
        ...base,
        readonly: (this.currentMode === 'consult' || this.isJobReadonly) ? true : base.readonly
      }
    },

    jobIdProps() {
      return { ...this.buildFieldProps('jobId', this.jobIdBaseProps) }
    },

    jobCampaignProps() {
      const base = this.buildFieldProps('jobCampaign', this.jobCampaignBaseProps)
      // Always return a new object to avoid modifying the base props
      return {
        ...base,
        readonly: (this.currentMode === 'consult' || this.isJobReadonly) ? true : base.readonly
      }
    },

    jobCustomerProps() {
      const base = this.buildFieldProps('jobCustomer', this.jobCustomerBaseProps)

      let customProps = {}
      if (this.currentMode != 'create') {
        // If job is already created, we disable changing customer to avoid complications with DAM permissions and links
        customProps.readonly = true
        customProps.clearable = false
      }

      // Always return a new object to avoid modifying the base props
      return {
        ...base,
        ...customProps,
      }
    },

    jobCustomerCodeProps() {
      const base = this.buildFieldProps('jobCustomerCode', this.jobCustomerCodeBaseProps)

      return {
        ...base,
        readonly: (this.currentMode === 'consult' || this.isJobReadonly) ? true : base.readonly
      }
    },

    jobDeadlineProps() {
      const base = this.buildFieldProps('jobDeadline', this.jobDeadlineBaseProps)
      // Always return a new object to avoid modifying the base props
      return {
        ...base,
        disabled: (this.currentMode === 'consult' || this.isJobReadonly) ? true : base.disabled
      }
    },

    isFileFieldReadonly() {
      const fileField = this.dynamicFormFields.find(field => field.type == "job-files")

      if (! fileField) {
        return true
      }

      return (this.localMode == 'consult' || this.isJobReadonly) ? true : fileField.readonly
    },

    dynamicFormNodeIds() {
      const jobNodeId = this.jobData.node_id ?? null
      const jobFolderNodeId = this.jobData.job_folder_id ?? null

      let nodeIds = [
        ...(jobNodeId !== null ? [jobNodeId] : []),
        ...(jobFolderNodeId !== null ? [jobFolderNodeId] : [])
      ]

      return nodeIds
    },

    allowedPools() {
      const MOVE_ACL = 1
      const allowedIds = (this.userAcls || [])
        .filter(acl => acl.acl & MOVE_ACL)
        .map(acl => acl.pool_id)

      return (this.pools || []).filter(p => allowedIds.includes(p.pool_id))
    },

    currentMode() {
      // Closed jobs are always displayed in consultation mode.
      return this.isJobReadonly ? 'consult' : this.localMode
    },

    appInstance() {
      return this.getUserApplicationById(this.$route.params.appId)
    },

    appConfig() {
      const cfg = this.appInstance && this.appInstance.config
      if (!cfg) return {}
      try { return typeof cfg === 'string' ? JSON.parse(cfg) : cfg } catch (e) { return {} }
    },

    modalWidth() {
      return this.isMaximized ? '100vw' : '60vw'
    },

    ownerLaneOptions() {
      return this.lanes.filter(l => this.userLanes.includes(l.lane_id) && l.pool_id == this.jobDefinition?.owner_pool_id)
    },

    // Move-after-save modal helpers
    selectedPool() {
      const list = this.pools || []
      return list.find(p => p.pool_id === this.moveForm.selectedPoolId) || null
    },

    moveFormStatuses() {
      if (!this.selectedPool) return []
      const ids = (this.selectedPool.statuses || []).map(st => st.status_id)
      return (this.statuses || []).filter(s => ids.includes(s.status_id))
    },

    moveFormLanes() {
      if (!this.selectedPool) return []
      const lanes = (this.lanes || []).filter(l => l.pool && l.pool.pool_id === this.moveForm.selectedPoolId)
      return lanes.map(l => ({ lane_id: l.lane_id, title: this.getGroupTitle(l.user_group_id) }))
    },

    commentsWidth() {
      return this.isMaximized ? '25%' : '33.33%'
    },

    // Pending placeholders for current panel
    pendingUploadsForCurrent() {
      const t = this.currentFileType || 1
      const arr = (this.pendingUploads && this.pendingUploads[t]) || []
      return Array.isArray(arr) ? arr : []
    },

    pendingLinksForCurrent() {
      const t = this.currentFileType || 1
      const arr = (this.pendingLinks && this.pendingLinks[t]) || []
      return Array.isArray(arr) ? arr : []
    },

    hasPendingForCurrent() {
      return this.pendingUploadsForCurrent.length > 0 || this.pendingLinksForCurrent.length > 0
    },

    currentFileDownloadParentId() {
      const type = this.currentFileType || 1
      const jobFolderId = type === 1 ? this.jobData.ref_folder_id : this.jobData.mat_folder_id
      return jobFolderId || this.currentDamStartNodeId[type === 1 ? 'references' : 'finals'] || null
    },

    jobRootNodeId() {
      // Try specific fields if backend provides job folders in jobData
      return this.jobData?.job_folder_id || this.jobData?.ref_folder_id || null
    },

    // Centralize rule for upload destination (can be changed later)
    // Upload destination per current/pending type
    uploadParentNodeId() {
      const t = this.pendingUploadType || this.currentFileType || 1
      if (t === 1) return this.jobData.ref_folder_id || null
      if (t === 2) return this.jobData.mat_folder_id || null
      return this.jobData.job_folder_id || null
    },

    damStartNodeId() {
      return this.jobDefinition?.job_root_destination_node_id || null
    },

    configuredDamStartNodeIds() {
      const defaultNodeId = this.damStartNodeId
      const jobFilesField = this.dynamicFormFields.find(field => field.type === 'job-files')

      return {
        references: jobFilesField?.referencesNodeId || defaultNodeId,
        finals: jobFilesField?.finalsNodeId || defaultNodeId
      }
    },

    currentLocale() {
      return this.$i18n.locale
    },

    formattedDeadline() {
      if (! this.jobData.deadline) {
        return
      }

      let date = this.jobData.deadline

      if (this.isDateTimeString(date)) {
        date = this.parseDate(date)
      } else {
        date = parseISO(date)
      }

      return format(date, 'dd/MM/yyyy')
    },

    canMoveToPools() {
      const MOVE_ACL = 1
      const allowedIds = (this.userAcls || [])
        .filter(acl => acl.acl & MOVE_ACL)
        .map(acl => acl.pool_id)

      return (this.pools || []).filter(p => allowedIds.includes(p.pool_id))
    },

    // Preselect pool/lane for MoveToLaneDialog (edit/update)
    moveInitialSelection() {
      const job = this.jobData || null
      if (!job) {
        return { poolId: null, statusId: null, laneId: null, slaHours: null, sumSLA: false }
      }

      const currentLane = (this.lanes || []).find(l => (l.lane_id || l.id) == job.lane_id) || null
      const currentPoolId = currentLane?.pool?.pool_id || null
      const allowedPoolIds = (this.allowedPools || []).map(p => p.pool_id)
      const selectedPoolId = allowedPoolIds.includes(currentPoolId)
        ? currentPoolId
        : ((this.allowedPools[0] && this.allowedPools[0].pool_id) || null)

      return {
        poolId: selectedPoolId,
        statusId: null,
        laneId: null,
        slaHours: null,
        sumSLA: false,
      }
    },

    // Display/edit helpers for dd/mm/aaaa format
    startDateDisplay: {
      get() { return this.formatDate(this.jobData.start_date) },
      set(v) { this.jobData.start_date = this.parseDateDisplay(v) }
    },
    endDateDisplay: {
      get() { return this.formatDate(this.jobData.end_date) },
      set(v) { this.jobData.end_date = this.parseDateDisplay(v) }
    },
  },

  watch: {
    jobData: {
      deep: true,
      handler() {
        try {
          if (this.suspendJobSync) return
          const jobForState = { ...(this.$store.state.job?.job || {}), ...this.jobData }
          this[JobMutations.UPDATE_JOB](jobForState)
        } catch (e) { /* ignore */ }
      }
    },
    ownerLaneOptions: {
      deep: true,
      immediate: true,
      handler(items) {
        if (!this.jobData.lane_id && items.length === 1) {
          this.jobData.lane_id = items[0].lane_id
        }
      }
    },
    'jobData.customer_id': {
      handler(newValue, oldValue) {
        // Loading an existing job also changes customer_id from the empty form
        // value. It must not clear the campaign persisted on that job.
        if (this.loadingJob) {
          if (newValue) {
            this.loadCampaigns()
          }
          return
        }

        // Only react if customer actually changed (not on initial load)
        if (oldValue !== undefined && newValue !== oldValue) {
          // Clear campaign selection and reload campaigns for the new customer
          this.jobData.campaign_id = null
          this.loadCampaigns()
          // Clear deliverables when customer changes
          this.$store.commit(`job/${JobMutations.CLEAR_TEMP_DELIVERABLES}`)
        } else if (newValue) {
          // On initial load with an existing customer, load their campaigns
          this.loadCampaigns()
        }
      }
    },
  },

  created() {
    this.fileDownloadBgActionHandler = this.handleSelectedFilesDownloadBgAction
    BgActionEventBus.$on('bgAction', this.fileDownloadBgActionHandler)
  },

  beforeDestroy() {
    if (this.fileDownloadBgActionHandler) {
      BgActionEventBus.$off('bgAction', this.fileDownloadBgActionHandler)
    }
  },

  methods: {
    ...mapActions('content/actions', [
      ContentActions.DOWNLOAD_FILE,
      ContentActions.UPLOAD_FILE,
      ContentActions.CREATE_FOLDER,
      ContentActions.SHARE_MULTI_NODE_DOWNLOAD,
    ]),

    ...mapActions('app', [
      AppActions.OPEN_APP_ERROR_MESSAGE,
    ]),

    ...mapActions('content/entry', [
      EntryActions.GET_ENTRY_DATA,
      EntryActions.LIST_SUBFOLDER,
    ]),


    ...mapActions('job', [
      JobActions.DELETE_JOB,
    ]),

    ...mapMutations('job', [
      JobMutations.UPDATE_JOB,
      JobMutations.REMOVE_JOB,
      JobMutations.REMOVE_SUBTASKS_BY_PARENT_JOB_ID,
    ]),

    ...mapActions('core/notification', [
      NotificationActions.SEND_MESSAGE,
    ]),

    ...mapActions('app', [
      AppActions.OPEN_APP_ERROR_MESSAGE,
    ]),

    validateDeliverablesFromStore() {
      const deliverables = this.tempDeliverables || []

      // If no deliverables, validation passes (deliverables are optional)
      if (deliverables.length === 0) {
        return true
      }

      // Validate each base instance
      for (const item of deliverables) {
        const baseInstance = item.base_instance || item

        if (!baseInstance.base_title || !baseInstance.base_title.trim()) {
          this[AppActions.OPEN_APP_ERROR_MESSAGE](
            this.$t('deliverable_title_required') || 'All deliverables must have a title.'
          )
          return false
        }

        if (!baseInstance.deliverable_id) {
          this[AppActions.OPEN_APP_ERROR_MESSAGE](
            this.$t('deliverable_type_required') || 'All deliverables must have a type selected.'
          )
          return false
        }

        if (!baseInstance.status_id) {
          this[AppActions.OPEN_APP_ERROR_MESSAGE](
            this.$t('deliverable_status_required') || 'All deliverables must have a status selected.'
          )
          return false
        }

        // Validate format instances if they exist
        const formatInstances = item.format_instances || []
        for (const formatItem of formatInstances) {
          const formatInstance = formatItem.format_instance || formatItem

          if (!formatInstance.format_desc || !formatInstance.format_desc.trim()) {
            this[AppActions.OPEN_APP_ERROR_MESSAGE](
              this.$t('format_desc_required') || 'All formats must have a description.'
            )
            return false
          }

          if (!formatInstance.format_title || !formatInstance.format_title.trim()) {
            this[AppActions.OPEN_APP_ERROR_MESSAGE](
              this.$t('format_title_required') || 'All formats must have a title.'
            )
            return false
          }

          if (!formatInstance.quantity || formatInstance.quantity <= 0) {
            this[AppActions.OPEN_APP_ERROR_MESSAGE](
              this.$t('format_quantity_must_be_positive') || 'Format quantity must be greater than 0.'
            )
            return false
          }

          if (!formatInstance.status_id) {
            this[AppActions.OPEN_APP_ERROR_MESSAGE](
              this.$t('format_status_required') || 'All formats must have a status selected.'
            )
            return false
          }

          // Validate billable instances if they exist
          const billableInstances = formatItem.billable_instances || []
          for (const billableInstance of billableInstances) {
            if (!billableInstance.billable_title || !billableInstance.billable_title.trim()) {
              this[AppActions.OPEN_APP_ERROR_MESSAGE](
                this.$t('billable_title_required') || 'All billable items must have a title.'
              )
              return false
            }

            if (!billableInstance.quantity || billableInstance.quantity <= 0) {
              this[AppActions.OPEN_APP_ERROR_MESSAGE](
                this.$t('billable_quantity_must_be_positive') || 'Billable quantity must be greater than 0.'
              )
              return false
            }
          }
        }
      }

      return true
    },

    openMoveModal() {
      if (this.isJobReadonly) return

      // Reset fields to blank and open modal
      this.moveForm = { selectedPoolId: null, selectedLaneId: null, selectedStatusId: null, slaHours: null, sumSLA: false }
      this.onChildModalOpen()
      this.showMoveDialog = true
    },

    async submitMoveFromEdit() {
      if (this.isJobReadonly) return

      this.saving = true
      try {
        // 1) Update job and related data using existing helper
        await this.saveUpdateJob()

        // 2) Move to selected pool/lane with optional SLA
        const payload = {
          job_id: this.jobData.job_id || this.jobId,
          pool_id: this.moveForm.selectedPoolId,
          lane_id: this.moveForm.selectedLaneId,
          status_id: this.moveForm.selectedStatusId,
          sum_sla: !!this.moveForm.sumSLA,
        }
        if (Number(this.moveForm.slaHours) > 0) payload.sla = Number(this.moveForm.slaHours)

        const res = await JobAPI.moveJobToPool(payload)
        const updated = { ...(this.jobData || {}), ...(res?.data || {}) }
        this[JobMutations.UPDATE_JOB](updated)

        this.$emit('job-updated', updated)
        this.$emit('success', 'Job updated successfully')
        this.showMoveDialog = false
        this.closeModal()
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('submitMoveFromEdit failed:', e?.message || e)
      } finally {
        this.saving = false
      }
    },

    handleMoveDialogConfirm(payload) {
      this.moveForm.selectedPoolId = payload.poolId
      this.moveForm.selectedStatusId = payload.statusId
      this.moveForm.selectedLaneId = payload.laneId
      this.moveForm.slaHours = payload.slaHours
      this.moveForm.sumSLA = !!payload.sumSLA
      this.submitMoveFromEdit()
    },

    getGroupTitle(groupId) {
      const g = (this.groups || []).find(gr => (gr.user_group_id || gr.id) == groupId)
      return g && g.group_title ? g.group_title : `${this.$t('cooperative')} ID: ${groupId}`
    },

    async openModal(jobDefinitionId = null, jobId = null, mode = null) {
      this.resetForm()

      this.suspendJobSync = true
      this.loadingJob = true
      this.showModal = true

      // Start with comments panel collapsed
      this.isCommentsCollapsed = true

      this.jobId = jobId
      this.jobData.job_definition_id = jobDefinitionId

      this.jobDeliverables = []
      this.selectedDeliverable = null
      this.deliverableDetailsDialog = false
      this.deliverableDetailsIndex = null
      this.deliverableDetails = { quantity: 1, instructions: '' }
      this.activePanel = 'form'
      this.currentFileType = 1

      this.initializeDamStartNodeId()

      this.loadTenantId()

      this.localMode = jobId ? (mode === 'consult' ? 'consult' : 'update') : 'create'
      if (jobId) {
        await this.loadJobData()
      }

      await this.loadCampaigns()
      this.loadCustomers()

      await this.loadStatusOptions()

      // Resume syncing to store and push current snapshot
      this.suspendJobSync = false

      this.originalTitle = this.jobData.job_title,
      this.originalCustomerCode = this.jobData.job_customer_code,
      this.originalDeadline = this.jobData.deadline,
      this.originalCampaign = "";
      for (const campaign of this.campaignOptions) {
        if (campaign.campaign_id === this.jobData.campaign_id) {
          this.originalCampaign = campaign.campaign_title;
          break;
        }
      }
      this.originalStatus = "";
      for (const status of this.statuses) {
        if (status.status_id === this.jobData.status_id) {
          this.originalStatus = status.status_title;
          break;
        }
      }

      // Store original deliverables for change tracking
      //this.originalDeliverables = JSON.parse(JSON.stringify(this.jobDeliverables))

      if (jobId) {
        const isExecutorInLane = this.userLanes.includes(this.jobData.lane_id)

        if (false == isExecutorInLane) {
          this.localMode = 'consult'
        }
      }

      this.loadingJob = false
    },

    closeModal() {
      // Keep Vuex state with last known job; avoid syncing resets
      this.suspendJobSync = true
      this.showModal = false
      this.clearFileSelection()
      this.resetForm()
    },

    openJobFilesFolder() {
      const contentsId = this.$route.params.contentAppId ||
        this.appConfig?.content_app_inst_id ||
        this.appConfig?.content_app_id || null

      const jobFolderId = this.jobData?.job_folder_id

      if (!contentsId || !jobFolderId) return

      const { href } = this.$router.resolve({
        name: 'ContentNavigation',
        params: { appId: contentsId, parentId: jobFolderId },
      })

      window.open(href, '_blank', 'noopener')
    },

    toggleMaximize() {
      this.isMaximized = !this.isMaximized
    },

    async loadCampaigns() {
      const customerId = this.jobData.customer_id
      if (!customerId) {
        this.campaignOptions = []
        return
      }

      try {
        const response = await CampaignAPI.getCampaignsByCustomer(customerId)
        const items = response?.data?.items || response?.data || []
        const curCampaignId = this.jobData.campaign_id
        this.campaignOptions = items.filter(campaign =>
          campaign.active || String(campaign.campaign_id) === String(curCampaignId)
        )

        // Preserve the API's id type so Vuetify can match v-model to
        // item-value even when the job endpoint returns it as a string.
        const selectedCampaign = this.campaignOptions.find(campaign =>
          String(campaign.campaign_id) === String(curCampaignId)
        )
        if (selectedCampaign) {
          this.jobData.campaign_id = selectedCampaign.campaign_id
        }
      } catch (error) {
        console.error('Error loading campaigns:', error)
        this.campaignOptions = []
      }
    },

    async loadCustomers() {
      try {
        let options = {}
        options['filter[active]'] = true
        const response = await CustomerAPI.getAllCustomers(options)

        this.customerOptions = response?.data?.items || response?.data || []
      } catch (error) {
        console.error('Error loading customers:', error)
        this.customerOptions = []
      }
    },

    async loadStatusOptions() {
      try {
        let jobDefinitionStatus = []
        let nextStatus = []
        this.poolStatuses = []

        await JobPoolStatusAPI.getJobPoolStatusByJobDefinition(this.jobData.job_definition_id)
          .then(async (result) => {
            jobDefinitionStatus = result.data.items || []
            this.poolStatuses = [...jobDefinitionStatus]
          })

        const ownerPoolId = this.jobDefinition?.owner_pool_id
        const targetPoolId = this.jobDefinition?.pool_id

        if (this.localMode === 'create' && ownerPoolId && ownerPoolId != targetPoolId) {
          const hasOwnerPoolStatuses = this.poolStatuses.some(jps => jps.pool_id == ownerPoolId)

          if (!hasOwnerPoolStatuses) {
            const ownerPoolStatus = await PoolStatusAPI.getPoolStatusesFromPool(ownerPoolId)
            this.poolStatuses = [...this.poolStatuses, ...ownerPoolStatus.data.items || []]
          }
        }

        let nextStatusPromises = []
        jobDefinitionStatus.forEach(async (jobPoolStatus) => {
            const nextStatusCall = NextStatusAPI.getByCurrentPoolStatus(jobPoolStatus.job_pool_status_id)
              .then((jobNextStatus) => {
                nextStatus = [...nextStatus, ...jobNextStatus.data.items]
              })

            nextStatusPromises.push(nextStatusCall)
          })

        await Promise.all(nextStatusPromises)

        let currentPoolId = this.jobDefinition.pool_id

        if (this.localMode == 'update') {
          currentPoolId = this.lanes.find(l => l.lane_id == this.jobData.lane_id).pool_id
        }

        let filteredJobPoolStatus = jobDefinitionStatus.filter(jps => jps.pool_id == currentPoolId)

        if (this.localMode == 'update') {
          const currentJobPoolStatus = filteredJobPoolStatus.find(jps => jps.status_id == this.jobData.status_id)
          this.currentJobPoolStatus = currentJobPoolStatus

          const possibleNextStatus = nextStatus
            .filter(ns => ns.source_job_pool_status_id == currentJobPoolStatus?.job_pool_status_id)
            .map(ns => ns.next_job_pool_status_id)

          if (possibleNextStatus?.length > 0) {
            filteredJobPoolStatus = filteredJobPoolStatus.filter(
              jds => possibleNextStatus.includes(jds.job_pool_status_id) || jds.job_pool_status_id == currentJobPoolStatus?.job_pool_status_id
            )
          }
        }

        const statusMap = new Map()
        this.statuses.forEach(status => {
          statusMap.set(status.status_id, {
            status_id: status.status_id,
            title: status.status_title,
            status_type: status.status_type
          })
        })

        let availableStatuses = []

        filteredJobPoolStatus.forEach((jobPoolStatus) => {
          const statusDetails = statusMap.get(jobPoolStatus.status_id)

          availableStatuses.push({
            ...statusDetails,
            pool_id: currentPoolId
          })
        })

        // Apply filtering based on mode
        if (this.localMode === 'create') {
          // For new jobs, only show "waiting" statuses
          this.statusOptions = availableStatuses.filter(status => status.status_type == 'waiting')

          // Set default status to first waiting status if available
          if (this.statusOptions.length > 0) {
            this.jobData.status_id = this.statusOptions[0].status_id
          }
        } else {
          // For updates, show all "can_move_to" pool statuses
          this.statusOptions = availableStatuses
        }
      } catch (error) {
        console.error('Error loading status options:', error)
      }
    },

    async loadJobData() {
      if (!this.jobId) return

      try {
        const response = await JobAPI.getJob(this.jobId)
        this.jobData = { ...this.jobData, ...response.data }
        this.jobData.campaign_id = this.getJobCampaignId(this.jobData)
        this.jobData.job_customer_code = this.getJobCustomerCode(this.jobData)

        this.jobData.deadline = this.normalizeDateOnly(this.jobData.deadline)
        this.initializeDamStartNodeId()

        // After job data is loaded, fetch job files
        if (this.jobData?.job_id) {
          await this.loadJobFiles()
        }
      } catch (error) {
        console.error('Error loading job data:', error)
        this.$emit('error', 'Failed to load job data')
      }
    },

    getLanePoolId(laneId) {
      const lane = (this.lanes || []).find(l => (l.lane_id || l.id) == laneId)

      return lane?.pool_id || lane?.pool?.pool_id || null
    },

    getStatusIdsByPool(poolId) {
      const definitionStatusIds = (this.poolStatuses || [])
        .filter(jps => jps.pool_id == poolId)
        .map(jps => jps.status_id)

      if (definitionStatusIds.length > 0) {
        return definitionStatusIds
      }

      const pool = (this.pools || []).find(pool => pool.pool_id == poolId)

      return (pool?.statuses || []).map(poolStatus => poolStatus.status_id)
    },

    getCreateStatusId(targetStatusId) {
      const originPoolId = this.getLanePoolId(this.jobData.lane_id) || this.jobDefinition?.owner_pool_id || this.jobDefinition?.pool_id
      const targetPoolId = this.jobDefinition?.pool_id || originPoolId

      if (!originPoolId || originPoolId == targetPoolId) {
        return targetStatusId
      }

      const originStatusIds = this.getStatusIdsByPool(originPoolId)
      const originWaitingStatus = (this.statuses || []).find(status =>
        originStatusIds.some(statusId => statusId == status.status_id) && status.status_type == 'waiting'
      )

      return originWaitingStatus?.status_id || originStatusIds[0] || targetStatusId
    },

    shouldMoveCreatedJobToPool() {
      const targetPoolId = this.jobDefinition?.pool_id
      const targetLaneId = this.jobDefinition?.lane_id ?? null
      const originLaneId = this.jobData.lane_id
      const originPoolId = this.getLanePoolId(originLaneId)

      return !!targetPoolId && (originPoolId != targetPoolId || (targetLaneId && originLaneId != targetLaneId))
    },

    async saveJob() {
      if (this.isJobReadonly) return

      const fieldsFormValid = this.$refs.fieldsForm ? this.$refs.fieldsForm.validate() : true
      if (!this.$refs.jobForm.validate() || !fieldsFormValid) return

      // Validate deliverables from store
      if (!this.validateDeliverablesFromStore()) {
        return
      }

      this.saving = true
      const isCreatingJob = this.localMode === 'create'

      try {
        let response
        if (isCreatingJob) {
          const fields = this.builtInFieldValueMap()

          // Add customer_id and customer_title to fields if customer is selected
          let customerTitle = ''
          if (this.jobData.customer_id) {
            const selectedCustomer = this.customerOptions.find(
              customer => customer.customer_id === this.jobData.customer_id
            )

            if (selectedCustomer) {
              customerTitle = selectedCustomer.customer_name || selectedCustomer.customer_title || ''
              fields.customer_id = this.jobData.customer_id
              fields.customer_title = customerTitle
            }
          }

          const targetStatusId = this.jobData.status_id
          const createStatusId = this.getCreateStatusId(targetStatusId)

          const jobPayload = {
            ...this.jobData,
            fields: fields,
            lane_id: this.jobData.lane_id,
            status_id: createStatusId,
            content_app_inst_id: this.appConfig?.content_app_inst_id || this.appConfig?.content_app_id || null,
            business_app_inst_id: this.appConfig?.business_app_inst_id || this.appConfig?.business_app_id || null,
            deliverable_list: this.sharedDeliverables || []
          }

          if (jobPayload.job_id === null) {
            delete jobPayload.job_id
          }

          response = await JobAPI.createJob(jobPayload)
          const createdJob = response.data

          let jobData = { ...response.data }

          // Update local job id so the read-only Jamworks ID field shows immediately
          this.jobData = {...this.jobData, status_id: createStatusId, ...jobData}
          this.jobId = jobData.job_id
          this.localMode = 'update'
          this.initializeDamStartNodeId()

          await this.$nextTick()

          await this.$refs.dynamicForm?.postSubmitAction()

          // create job history
          try {
            const commentData = {
              job_id: this.jobData.job_id,
              message: "<b>Job criado</b>",
              comment_type: 2
            }

            await CommentAPI.createComment(commentData)
          } catch (error) {
            console.error('Error creating history comment:', error)
          }

          // Apply queued uploads/links now that job exists
          await this.applyPendingFileOps(createdJob?.job_id || createdJob?.id)

          if (this.shouldMoveCreatedJobToPool()) {
            const movePayload = {
              pool_id: this.jobDefinition.pool_id,
              lane_id: this.jobDefinition.lane_id ?? null,
              job_id: jobData.job_id,
              status_id: targetStatusId,
            }

            const moveResponse = await JobAPI.moveJobToPool(movePayload)
            this.jobData = { ...this.jobData, ...(moveResponse?.data || {}) }
            this.initializeDamStartNodeId()
          }

          let context = {
            jobTitle: this.jobData.job_title,
            poolId: this.jobDefinition.pool_id
          }

          if (this.jobData.parent_job_id) {
            context.jobTitle = `${this.jobData.job_title} - ${this.jobData?.fields?.parent_job_title}`
          }

          this.notifyJobEnteredLane(this.jobData, context)

          this.$emit('job-created', this.jobData)
        } else if (this.localMode === 'update') {
          const updatedJob = await this.saveUpdateJob()
          this.$emit('job-updated', updatedJob)
        }

        this.$emit('success', isCreatingJob ? 'Job created successfully' : 'Job updated successfully')
        this.closeModal()
      } catch (error) {
        console.error('Error saving job:', error)
        this.$emit('error', error)
      } finally {
        this.saving = false
      }
    },

    async saveUpdateJob() {
      if (this.isJobReadonly) return

      var newCampaign = "";
      for (const campaign of this.campaignOptions) {
        if (campaign.campaign_id === this.jobData.campaign_id) {
          newCampaign = campaign.campaign_title;
          break;
        }
      }

      var newStatus = "";
      for (const status of this.statuses) {
        if (status.status_id === this.jobData.status_id) {
          newStatus = status.status_title;
          break;
        }
      }

      var history = this.setHistory("", "título", this.originalTitle, this.jobData.job_title)
      history = this.setHistory(history, "código", this.originalCustomerCode, this.jobData.job_customer_code)
      history = this.setHistory(history, "prazo", this.originalDeadline, this.jobData.deadline)
      history = this.setHistory(history, "campanha", this.originalCampaign, newCampaign)
      history = this.setHistory(history, "status", this.originalStatus, newStatus)

      if (history.length > 0) {
        try {
          const commentData = {
            job_id: this.jobId,
            message: history,
            comment_type: 2
          }
          await CommentAPI.createComment(commentData)
        } catch (error) {
          console.error('Error creating history comment:', error)
        }
      }

      const updatePayload = {
        ...this.jobData,
        fields: this.builtInFieldValueMap(),
        deliverable_list: this.sharedDeliverables || []
      }

      const response = await JobAPI.updateJob(this.jobId, updatePayload)

      this.updateTerminalStatusVisibility()

      await this.$refs.dynamicForm?.postSubmitAction()

      await this.applyPendingFileOps(this.jobId)

      return response
    },

    updateTerminalStatusVisibility() {
      const status = this.statuses.find(status => status.status_id == this.jobData.status_id)
      if (!['done', 'canceled'].includes(status?.status_type)) return

      const jobId = this.jobData.job_id || this.jobId

      if (this.jobData.parent_job_id) {
        this[JobMutations.REMOVE_JOB](jobId)
        return
      }

      this[JobMutations.REMOVE_SUBTASKS_BY_PARENT_JOB_ID](jobId)
    },

    fieldValueMap() {
      if (! this.$refs.fieldsForm) {
        return {}
      }

      const formData = new FormData(this.$refs.fieldsForm.$el)

      return Array.from(formData)
        .filter(([key]) => key !== 'field_check[]')
        .reduce((obj, [key, value]) => {
            obj[key] = value
            return obj
        }, {})
    },

    builtInFieldValueMap() {
      const fields = this.fieldValueMap()
      const customerCode = this.jobData.job_customer_code

      if (customerCode !== null && customerCode !== undefined) {
        fields.job_customer_code = customerCode
      }

      return fields
    },

    getJobCustomerCode(job) {
      return job?.job_customer_code
        ?? job?.fields?.job_customer_code
        ?? ''
    },

    getJobCampaignId(job) {
      const campaignId = job?.campaign_id
      if (campaignId !== null && campaignId !== undefined && campaignId !== 0 && campaignId !== '0') {
        return campaignId
      }

      return job?.fields?.campaign_id ?? campaignId ?? null
    },

    async saveAndMoveToLane(moveData) {
      if (this.isJobReadonly) return

      if (!this.$refs.jobForm.validate()) return

      this.saving = true

      let updatedJob = await this.saveUpdateJob()

      try {
        const movePayload = {
          job_id: this.jobId,
          pool_id: moveData.pool.pool_id,
          lane_id: moveData.toLane.lane_id,
          status_id: moveData.status_id,
          sla: moveData.sumSLA || false,
          pool_title: moveData.pool.pool_title
        }

        const moveResponse = await JobAPI.moveJobToPool(movePayload)
        let moveContext = {
          jobTitle: this.jobData.job_title,
          poolId: moveData.pool.pool_id
        }

        if (this.jobData.parent_job_id) {
          moveContext.jobTitle = `${this.jobData?.fields?.parent_job_title} - ${this.jobData.job_title}`
        }

        updatedJob = { ...updatedJob, ...(moveResponse?.data || {}) }

        this.notifyJobEnteredLane(moveResponse?.data || {}, moveContext)
      } catch (e) {
        console.warn('Error moving job to pool: ', e?.message || e)
      }

      this.$emit('job-updated', updatedJob)

      this.saving = false

      this.closeModal()
    },

    async notifyJobEnteredLane(result, context = {}) {
      try {
        const recipientsResult = await JobAPI.getCustomerVisibleActors(
          result.job_id,
          result.lane_id
        )
        const usersIds = recipientsResult.data?.users_ids ?? []
        const groupsIds = recipientsResult.data?.user_group_ids ?? []

        const jobTitle = context.jobTitle
        const startDateStr = (result && result.start_date)
        const link = this.buildAgendaLinks(context.startDate || startDateStr)

        const subject = this.$t('job_entered_lane_subject', { jobTitle })
        const content = this.$t('job_entered_lane_content', { jobTitle, link })

        const options = {
          subject,
          content,
          users_ids: usersIds,
          groups_ids: groupsIds,
        }

        this[NotificationActions.SEND_MESSAGE](options)
      } catch (e) {
        console.error(e)
      }
    },

    buildAgendaLinks(dateStr) {
      const appId = this.$route.params.appId
      const contentAppId = this.$route.params.contentAppId || 1
      const route = this.$router.resolve({ name: 'JobHomeContent', params: { appId, contentAppId } })

      const url = new URL(route.href, window.location.origin)
      url.searchParams.set('timelineInterval', '2') // 3 dias
      const dateParam = this.formatDateParamForURL(dateStr)
      if (dateParam) url.searchParams.set('date', dateParam)
      return url.href
    },

    formatDateParamForURL(dateInput) {
      try {
        let d
        if (dateInput instanceof Date) {
          d = dateInput
        } else if (typeof dateInput === 'string') {
          // Try parse using existing parser
          d = this.parseJobDate(dateInput)
        } else {
          d = new Date()
        }
        if (isNaN(d)) return null

        const pad = (n) => (n < 10 ? '0' + n : '' + n)
        const yyyy = d.getFullYear()
        const mm = pad(d.getMonth() + 1)
        const dd = pad(d.getDate())
        const HH = pad(d.getHours())
        const MM = pad(d.getMinutes())
        const SS = pad(d.getSeconds())
        return `${yyyy}/${mm}/${dd}-${HH}:${MM}:${SS}`
      } catch (_) { return null }
    },

    parseJobDate(dateString) {
      if (!dateString) return new Date()

      try {
        // First try standard ISO format
        if (dateString.includes('T')) {
          return parseISO(dateString)
        }

        // Handle format: 2025-09-15 10:00:00+0000
        // This is actually Brazil local time, not UTC despite the +0000
        // Extract date and time parts
        const [datePart, timePart] = dateString.split(' ')
        const [year, month, day] = datePart.split('-').map(Number)
        const [hour, minute, second] = timePart.replace(/\+\d{4}$/, '').split(':').map(Number)

        // Create date in local timezone (Brazil)
        return new Date(year, month - 1, day, hour, minute, second || 0)
      } catch (error) {
        console.warn('Error parsing job date:', error, dateString)
        // Fallback: try to parse as regular date
        return new Date(dateString)
      }
    },

    setHistory(history, attribute, oldValue, newValue) {
      if (oldValue != newValue) {
        if (history.length > 0) {
          history += ", "
        }
        history += `O <b>${attribute}</b> foi alterado para <b>${newValue}</b>`
      }
      return history
    },

    // Ensure a subfolder with given name exists under parent; returns node_id
    async ensureChildFolder(parentId, name) {
      if (!parentId || !name) return null
      const safe = this.sanitizeFolderName(name)
      try {
        const list = await this[EntryActions.LIST_SUBFOLDER]({ parentId, updateList: false })
        const entries = list?.entries || []
        const found = entries.find(e => e.type === 'folder' && (e.name === safe || e.name === name))
        if (found) return found.node_id
      } catch (e) { /* ignore and try create */ }
      try {
        const res = await this[ContentActions.CREATE_FOLDER]({ folder_parent_id: parentId, name: safe })
        return res?.node_id || res?.nodeId || res?.id || null
      } catch (e) {
        console.warn('ensureChildFolder create error', e?.message || e)
        return null
      }
    },

    sanitizeFolderName(name) {
      if (!name) return 'Untitled'
      let str = String(name)
      // Replace slashes/backslashes
      str = str.replace(/[\\/]+/g, ' - ')
      // Remove control chars without using control-char regex (eslint no-control-regex)
      let cleaned = ''
      for (let i = 0; i < str.length; i++) {
        const code = str.charCodeAt(i)
        if ((code >= 32 && code !== 127)) cleaned += str[i]
      }
      return cleaned
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 200)
    },

    async ensureJobFoldersExist() {
      if (!this.jobData?.job_id) return

      // Ensure subfolders exist under an existing job folder
      try {
        const result = await this.ensureSubfolders(this.jobData.job_folder_id)
        const patch = {}
        if (!this.jobData.ref_folder_id && result['Referências']) patch.ref_folder_id = result['Referências']
        if (!this.jobData.mat_folder_id && result['Finais']) patch.mat_folder_id = result['Finais']
        if (Object.keys(patch).length) {
          await JobAPI.updateJob(this.jobData.job_id || this.jobId, patch)
          this.jobData = { ...this.jobData, ...patch }
          this.initializeDamStartNodeId()
        }
      } catch (e) {
        console.warn('ensureJobFoldersExist subfolders error', e?.message || e)
      }
    },

    async ensureSubfolders(parentId) {
      const wanted = ['Referências', 'Finais', 'Intermediários', 'Ajustes Revisão']
      const map = {}
      try {
        const list = await this[EntryActions.LIST_SUBFOLDER]({ parentId, updateList: false })
        const entries = list?.entries || []
        // Find existing
        wanted.forEach(name => {
          const found = entries.find(e => e.type === 'folder' && e.name === name)
          if (found) map[name] = found.node_id
        })
        // Create missing
        for (const name of wanted) {
          if (!map[name]) {
            const res = await this[ContentActions.CREATE_FOLDER]({ folder_parent_id: parentId, name })
            const nid = res?.node_id || res?.nodeId || res?.id
            if (nid) map[name] = nid
          }
        }
      } catch (e) {
        console.warn('ensureSubfolders error', e?.message || e)
      }
      return map
    },

    computeJobFolderName(job) {
      const id = job?.job_id || this.jobId || ''
      const title = job?.job_title || 'Job'
      const base = `${id} - ${title}`.trim()
      return this.sanitizeFolderName(base)
    },

    resetForm() {
      this.jobData = {
        job_id: null,
        job_definition_id: null,
        lane_id: null,
        requester_users_id: null,
        job_title: '',
        job_customer_code: '',
        status_id: 0,
        customer_id: null,
        campaign_id: 0,
        decided_sla: 0,
        start_date: null,
        end_date: null,
        job_folder_id: 0,
        ref_folder_id: 0,
        mat_folder_id: 0,
        deadline: null,
        readonly: false,
      }

      this.currentJobPoolStatus = null
      this.isMaximized = false
      this.isCommentsCollapsed = true
      this.pendingUploads = { 1: [], 2: [] }
      this.pendingLinks = { 1: [], 2: [] }
      // Clear shared deliverables from Vuex store
      this.$store.commit(`job/${JobMutations.CLEAR_TEMP_DELIVERABLES}`)
      if (this.$refs.jobForm) {
        this.$refs.jobForm.resetValidation()
      }
    },

    openCalendar() {
      this.selectedDate = this.jobData.deadline || null
      this.showCalendar = true
    },

    selectDate(date) {
      this.jobData.deadline = date || null
      this.showCalendar = false
    },

    // Format YYYY-MM-DD[THH:mm] -> DD/MM/YYYY for display-only fields
    formatDate(dateString, formatString = 'dd/MM/yyyy - HH:mm') {
      if (!dateString) return ''
      try {
        // Handle the format: 2025-09-15 10:00:00+0000
        const parsedDate = this.parseDate(dateString)
        return format(parsedDate, formatString)
      } catch (error) {
        console.warn('Error formatting job time:', error, dateString)
        return dateString
      }
    },

    parseDate(dateString) {
      if (!dateString) return new Date()

      try {
        // First try standard ISO format
        if (dateString.includes('T')) {
          return parseISO(dateString)
        }

        // Handle format: 2025-09-15 10:00:00+0000
        // This is actually Brazil local time, not UTC despite the +0000
        // Extract date and time parts
        const [datePart, timePart] = dateString.split(' ')
        const [year, month, day] = datePart.split('-').map(Number)
        const [hour, minute, second] = timePart.replace(/\+\d{4}$/, '').split(':').map(Number)

        // Create date in local timezone (Brazil)
        return new Date(year, month - 1, day, hour, minute, second || 0)
      } catch (error) {
        console.warn('Error parsing job date:', error, dateString)
        // Fallback: try to parse as regular date
        return new Date(dateString)
      }
    },

    isDateTimeString(dateString) {
      const timeIndicators = /[T\s]\d{2}:\d{2}(:\d{2}(\.\d{3})?)?/; // T or space, HH:MM, optional :SS, optional .SSS
      return timeIndicators.test(dateString);
    },

    // Normalize to YYYY-MM-DD (no time) for date inputs
    normalizeDateOnly(val) {
      if (!val) return null
      try {
        const s = String(val)
        return s.split('T')[0]
      } catch (e) { return val }
    },

    // Parse dd/mm/aaaa -> YYYY-MM-DD; returns null on invalid
    parseDateDisplay(v) {
      if (!v) return null
      const s = String(v).trim()
      const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
      if (!m) return null
      const d = m[1].padStart(2, '0')
      const mo = m[2].padStart(2, '0')
      const y = m[3]
      return `${y}-${mo}-${d}`
    },

    // Simple dd/mm/aaaa validator for text fields
    dateDisplayRule(v) {
      if (!v) return true
      const s = String(v).trim()
      return (/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/).test(s) || this.$t('form_rules.invalid_date') || 'Data inválida'
    },

    // Files panel controls (single drawer)
    openFilesPanel(payload) {
      let configuredNodeId = null

      // Handle both old format (just type) and new format (object with type and damStartNodeId)
      if (typeof payload === 'number') {
        // Legacy format: just the type
        this.currentFileType = payload
      } else if (payload && typeof payload === 'object') {
        // New format: { type, damStartNodeId }
        this.currentFileType = payload.type || 1
        configuredNodeId = payload.damStartNodeId
      }

      const type = this.currentFileType || 1
      const startNodeId = this.resolveContentSelectorStartNodeId(configuredNodeId)

      if (type === 1) {
        this.currentDamStartNodeId.references = startNodeId
      } else if (type === 2) {
        this.currentDamStartNodeId.finals = startNodeId
      }

      this.clearFileSelection()
      this.activePanel = 'files'
      if (this.jobData?.job_id) {
        this.loadJobFiles(this.currentFileType)
      }
    },

    backToForm() {
      this.clearFileSelection()
      this.activePanel = 'form'
    },

    openDeliverablesPanel() {
      this.activePanel = 'deliverables'
    },

    openDeliverableAssetsListPanel() {
      this.activePanel = 'deliverableAssetsList'
    },

    openDeliverableAssetsPanel() {
      this.activePanel = 'deliverableAssets'
    },

    onDeliverablesChanged(deliverables) {
      // Update shared deliverables state
      this.sharedDeliverables = deliverables || []
    },

    // Files: UI triggers
    async openLinkModal(type) {
      const selectedType = type || this.currentFileType || 1
      const isRef = (selectedType === 1)
      const refName = isRef ? 'selectRef' : 'selectMat'
      const configured = this.configuredDamStartNodeIds
      const configuredNodeId = isRef ? configured.references : configured.finals
      const startNodeId = this.resolveContentSelectorStartNodeId(configuredNodeId)

      if (isRef) {
        this.currentDamStartNodeId.references = startNodeId
      } else {
        this.currentDamStartNodeId.finals = startNodeId
      }

      // Ensure we have the latest list for this type to avoid duplicates
      if (this.jobData?.job_id) {
        try { await this.loadByType(selectedType) } catch (_) { /* ignore */ }
      }
      // Force remount to clear any stale internal state in selector
      if (isRef) this.selectRefKey++
      else this.selectMatKey++
      this.onChildModalOpen()
      this.$nextTick(() => {
        const comp = this.$refs[refName]
        if (comp && typeof comp.selectFile === 'function') comp.selectFile()
      })
    },

    // Upload modal flow for references
    async openUploadModal(type) {
      this.pendingUploadType = type || 1
      this.resetUploadBehavior()
      this.onChildModalOpen()
      if (this.$refs.uploadModal) this.$refs.uploadModal.openModal()
    },

    async makeUpload() {
      const files = this.$refs.uploadFileField?.getFiles?.() || []
      if (!files.length) return

      const type = this.pendingUploadType || this.currentFileType || 1
      // Queue files to upload on Save
      this.pendingUploads[type] = [...this.pendingUploads[type], ...files]

      if (this.$refs.uploadModal) this.$refs.uploadModal.closeModal()
      this.onChildModalClose()
    },

    // Validation and helpers for upload modal
    validateFileSelected(files) {
      return (files && files.length > 0) || this.$t('form_rules.mandatory');
    },

    validateFilename(files) {
      if (!files || files.length === 0) return true;
      // eslint-disable-next-line no-control-regex
      const invalidChars = /[\\/:*?"<>|\x00-\x1F\x7F]/;
      const reservedNames = ['CON','PRN','AUX','NUL','COM1','COM2','COM3','COM4','COM5','COM6','COM7','COM8','COM9','LPT1','LPT2','LPT3','LPT4','LPT5','LPT6','LPT7','LPT8','LPT9'];
      for (const file of files) {
        const name = file.name;
        const baseName = name.replace(/\.[^/.]+$/, '');
        if (invalidChars.test(name) || name.length > 255 || reservedNames.includes(baseName.toUpperCase()) || /[. ]$/.test(name)) {
          return this.$t('form_rules.invalid_filename');
        }
      }
      return true;
    },

    setUploadBehavior() {
      this.resetUploadBehavior()
      const files = this.$refs.uploadFileField?.getFiles?.() || []

      // Duplicate name validation was removed. The upload endpoints now
      // automatically append a suffix to files with a conflicting name.
      this.modalSaveButtonDisabled = !files.length
    },


    resetUploadBehavior() {
      this.uploadErrorMessage = ''
      this.modalSaveButtonDisabled = true
    },

    onChildModalOpen() {
      this.childModalOpen = true
    },

    onChildModalClose() {
      this.childModalOpen = false
      this.resetUploadBehavior()
    },

    removePendingUpload(type, index) {
      if (!this.pendingUploads[type]) return
      this.pendingUploads[type].splice(index, 1)
    },

    removePendingLink(type, index) {
      if (!this.pendingLinks[type]) return
      this.pendingLinks[type].splice(index, 1)
    },

    async onSelectNodesForLink(type, nodes) {
      if (!Array.isArray(nodes)) return
      const pending = Array.isArray(this.pendingLinks[type]) ? this.pendingLinks[type] : []
      const pendingSet = new Set(pending.map(n => n?.node_id || n).filter(Boolean))
      const alreadyLinkedSet = new Set(
        (this.referencesType === type && Array.isArray(this.references) ? this.references : [])
          .map(r => r.node_id)
          .filter(Boolean)
      )
      const toAdd = nodes.filter(n => {
        if (!n || (n.type || n.node_type) !== 'file') return false
        const id = n && (n.node_id || n)
        return id && !pendingSet.has(id) && !alreadyLinkedSet.has(id)
      })
      this.pendingLinks[type] = [...pending, ...toAdd]
      this.onChildModalClose()
    },

    confirmUnlink(item) {
      this.itemToUnlink = item
      this.onChildModalOpen()
      if (this.$refs.modalUnlink) {
        this.$refs.modalUnlink.openModal()
      }
    },

    async executeUnlink() {
      if (!this.itemToUnlink?.job_file_id) return
      try {
        await JobFileAPI.deleteJobFile(this.itemToUnlink.job_file_id)
        await this.loadJobFiles()
        if (this.$refs.modalUnlink) {
          this.$refs.modalUnlink.closeModal()
        }
        this.onChildModalClose()
      } catch (e) {
        console.error(e)
      } finally {
        this.itemToUnlink = null
      }
    },

    download(entry) {
      this[ContentActions.DOWNLOAD_FILE]({ nodeId: entry.node_id, control: { downloading: false } })
    },

    toggleFileSelection(nodeId) {
      if (!nodeId) return

      if (this.selectedFileNodeIds.includes(nodeId)) {
        this.selectedFileNodeIds = this.selectedFileNodeIds.filter(id => id !== nodeId)
        return
      }

      this.selectedFileNodeIds.push(nodeId)
    },

    clearFileSelection() {
      this.selectedFileNodeIds = []
      const fileCards = this.$refs.jobFileCards
      const cards = Array.isArray(fileCards) ? fileCards : [fileCards]

      cards.filter(Boolean).forEach(card => card.markSelectionAs(false))
    },

    async downloadSelectedFiles() {
      const parentNodeId = this.currentFileDownloadParentId
      const nodeIds = [...new Set(this.selectedFileNodeIds)].filter(Boolean)

      if (!parentNodeId || !nodeIds.length || this.fileDownloadInProgress) {
        return
      }

      const bgActionId = `bg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
      // The Content socket is folder-scoped. Register before the request so a
      // fast ZIP completion cannot be received before this panel is listening.
      BgActionEventBus.$emit('register-room', parentNodeId)
      this.pendingFileDownloadActions.add(bgActionId)
      this.fileDownloadInProgress = true

      try {
        await this[ContentActions.SHARE_MULTI_NODE_DOWNLOAD]({
          parentNodeId,
          nodeIds,
          bgActionId,
          zipName: this.getSelectedFilesZipName(nodeIds.length),
        })
      } catch (error) {
        this.pendingFileDownloadActions.delete(bgActionId)
        this.fileDownloadInProgress = false
        this[AppActions.OPEN_APP_ERROR_MESSAGE](error.response?.data?.message || error.message)
      }
    },

    handleSelectedFilesDownloadBgAction(message) {
      const bgActionId = message.bgActionId ?? message.bg_action_id
      if (!this.pendingFileDownloadActions.has(bgActionId)) {
        return
      }

      const status = String(message.status || '').toLowerCase()
      if (status === 'error') {
        this.pendingFileDownloadActions.delete(bgActionId)
        this.fileDownloadInProgress = false
        this[AppActions.OPEN_APP_ERROR_MESSAGE](message.error || message.text || this.$t('error'))
        return
      }

      if (!['done', 'success'].includes(status)) {
        return
      }

      const downloadUrl = this.getSelectedFilesZipDownloadUrl(message)
      if (!downloadUrl) {
        this.pendingFileDownloadActions.delete(bgActionId)
        this.fileDownloadInProgress = false
        this[AppActions.OPEN_APP_ERROR_MESSAGE](this.$t('error'))
        return
      }

      // Remove the id before opening the browser: socket events can be replayed.
      this.pendingFileDownloadActions.delete(bgActionId)
      this.fileDownloadInProgress = false
      const link = document.createElement('a')
      link.target = '_blank'
      link.rel = 'noopener'
      link.href = downloadUrl
      link.click()
    },

    getSelectedFilesZipName(fileCount) {
      const jobTitle = String(this.jobData?.job_title || 'job').trim()
        .replace(/[\\/:*?"<>|]/g, '-')
      const category = this.currentFileType === 1 ? this.$t('references') : this.$t('finals')
      return `${jobTitle || 'job'}_${category}_${fileCount}_files.zip`
    },

    getSelectedFilesZipDownloadUrl(message) {
      let payload = message.text
      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload)
        } catch (error) {
          return null
        }
      }

      const downloadUrl = payload?.download_url || payload?.downloadUrl
      if (typeof downloadUrl !== 'string') {
        return null
      }

      const markdownUrl = downloadUrl.match(/^\[[\s\S]*\]\((https?:\/\/[^)]+)\)$/)
      try {
        return new URL(markdownUrl ? markdownUrl[1] : downloadUrl).toString()
      } catch (error) {
        console.error('[share-download] invalid ZIP download URL', { message, error })
        return null
      }
    },

    async loadJobFiles(type = null) {
      // Guard: do not call API while job is not created yet
      if (!this.jobData?.job_id) return
      try {
        const t = type || this.currentFileType || 1
        await this.loadByType(t)
      } catch (e) { console.error(e) }
    },

    async loadByType(type) {
      if (!this.jobData?.job_id) return
      this.loadingReferences = true
      try {
        const resp = await JobFileAPI.listJobFilesByType(this.jobData.job_id, type, { order: 'job_file_id', direction: 'ASC', offset: 0, limit: 2000000000 })
        const items = resp?.data?.items || resp?.data || []
        const enriched = []
        for (const it of items) {
          try {
            const entry = await this[EntryActions.GET_ENTRY_DATA](it.node_id)
            enriched.push({ job_file_id: it.job_file_id || it.id, node_id: it.node_id, entry, _key: `${type}-${it.job_file_id || it.id}` })
          } catch (e) { /* ignore */ }
        }
        this.references = enriched
        this.referencesType = type
        this.clearFileSelection()
      } finally {
        this.loadingReferences = false
      }
    },

    async applyPendingFileOps(jobId) {
      try {
        if (!jobId) return
        const parentFor = (type) => {
          // 1 = Referências -> sempre na pasta Referencias
          if (type === 1) return this.jobData.ref_folder_id || null
          // 2 = Materiais -> sempre na pasta Materias
          if (type === 2) return this.jobData.mat_folder_id || null
          // fallback genérico (não utilizado na UI atual)
          return this.jobData.job_folder_id || null
        }
        for (const type of [1, 2]) {
          const files = this.pendingUploads[type] || []
          if (!files.length) continue
          const parent = parentFor(type)
          if (!parent) continue
          for (const file of files) {
            try {
              const result = await this[ContentActions.UPLOAD_FILE]({ parentId: parent, file })
              const nodeId = result && (result.nodeId || result.node_id || result.id)
              if (nodeId) {
                await JobFileAPI.createJobFile({ job_id: jobId, node_id: nodeId, job_file_type: type })
              }
            } catch (e) { console.error('queued upload failed', e) }
          }
        }
        for (const type of [1, 2]) {
          const nodes = this.pendingLinks[type] || []
          if (!nodes.length) continue
          // Fetch existing links to avoid creating duplicates
          let existingIds = []
          try {
            const resp = await JobFileAPI.listJobFilesByType(jobId, type, { order: 'job_file_id', direction: 'ASC', offset: 0, limit: 2000000000 })
            existingIds = (resp?.data?.items || resp?.data || []).map(it => it.node_id).filter(Boolean)
          } catch (_) { existingIds = [] }
          const existingSet = new Set(existingIds)
          const seen = new Set()
          for (const n of nodes) {
            try {
              const nodeId = (n && n.node_id) || n
              if (!nodeId) continue
              if (existingSet.has(nodeId)) continue
              if (seen.has(nodeId)) continue
              await JobFileAPI.createJobFile({ job_id: jobId, node_id: nodeId, job_file_type: type })
              seen.add(nodeId)
            } catch (e) { console.error('queued link failed', e) }
          }
        }
        this.pendingUploads = { 1: [], 2: [] }
        this.pendingLinks = { 1: [], 2: [] }
        await this.loadJobFiles()
      } catch (e) {
        console.warn('applyPendingFileOps error', e && (e.message || e))
      }
    },

    getUserGroupTitle(userGroupId) {
      const userGroup = this.groups.find(ug => ug.user_group_id === userGroupId)
      return userGroup ? userGroup.group_title : userGroupId
    },

    getCampaignTitle(campaignId) {
      const c = this.campaignOptions.find(c => (c.campaign_id || c.id) === campaignId)
      return c ? (c.campaign_title || c.title || c.name) : campaignId
    },

    loadTenantId() {
      try {
        // Get the application instance ID from the route
        const appId = this.$route.params.appId
        if (!appId) {
          return
        }

        // Get the application instance from the store using the getter
        const applicationInstance = this.$store.getters['app/getApplicationInstanceById'](appId)
        if (!applicationInstance) {
          return
        }

        // Handle different data structures
        let actualInstance = applicationInstance

        // If applicationInstance has items property, find the correct instance
        if (applicationInstance.items && Array.isArray(applicationInstance.items)) {
          actualInstance = applicationInstance.items.find(item =>
            item.application_instance_id == appId
          )
        }

        if (!actualInstance) {
          console.warn('Actual application instance not found')
          return
        }

        // Extract tenant ID from the config JSON
        if (actualInstance.config) {
          let config

          // Parse config if it's a string, otherwise use it directly
          if (typeof actualInstance.config === 'string') {
            try {
              config = JSON.parse(actualInstance.config)
            } catch (parseError) {
              console.error('Error parsing application instance config JSON:', parseError)
              return
            }
          } else {
            config = actualInstance.config
          }

          // Extract Tenant_id from config
          if (config && config.Tenant_id) {
            this.tenantId = config.Tenant_id
          }
        }
      } catch (error) {
        console.error('Error loading tenant ID:', error)
      }
    },

    onDiagramStepClicked(step) {
      this.jobData.diagram = step
    },

    onDatePickerMenuChange(isOpen) {
      // When opening the date picker, initialize tempDeadline with current value
      if (isOpen) {
        this.tempDeadline = this.jobData.deadline || null
      }
    },

    cancelDeadline() {
      // Close the menu without saving
      this.datePickerMenu = false
    },

    saveDeadline() {
      if (this.isJobReadonly) {
        this.datePickerMenu = false
        return
      }

      // Explicitly set jobData.deadline to the selected value using Vue.set for reactivity
      if (this.tempDeadline) {
        this.$set(this.jobData, 'deadline', this.tempDeadline)
      }
      // Close the menu
      this.datePickerMenu = false
    },

    openMoveToLaneDialog(pool) {
      if (this.isJobReadonly) return

      this.$refs.moveToLaneDialog.openModal(pool)
    },

    showDeleteConfirmation() {
      if (this.isJobReadonly) return

      this.deleteConfirmDialog = true
    },

    cancelDelete() {
      this.deleteConfirmDialog = false
    },

    async confirmDelete() {
      if (this.isJobReadonly) return

      if (!this.jobData.job_id && !this.jobId) {
        this.$emit('error', 'No job ID available for deletion')
        return
      }

      this.deleting = true

      try {
        const jobIdToDelete = this.jobData.job_id || this.jobId

        try {
          const commentData = {
            job_id: jobIdToDelete,
            message: "<b>Job deletado</b>",
            comment_type: 2
          }
          await CommentAPI.createComment(commentData)
        } catch (error) {
          console.warn('Error creating history comment for deletion:', error)
        }

        // Use Vuex action to delete job and update store
        await this[JobActions.DELETE_JOB](jobIdToDelete)

        this.$emit('job-deleted', jobIdToDelete)
        this.$emit('success', 'Job deleted successfully')
        this.deleteConfirmDialog = false
        this.closeModal()
      } catch (error) {
        console.error('Error deleting job:', error)
        this.$emit('error', 'Failed to delete job')
      } finally {
        this.deleting = false
      }
    },

    parseFormString(string) {
      try {
        return JSON.parse(string)
      } catch (e) {
        return {}
      }
    },

    isNotEmptyJson(value) {
      if (Array.isArray(value)) {
        return value.length > 0;
      } else if (typeof value === 'object' && value !== null) {
        return Object.keys(value).length > 0;
      }

      return false;
    },

    buildFieldProps(name, base) {
      const form = this.parsedJobDefinitionForm
      if (!form || !Array.isArray(form)) return base

      const override = form.find(i => i.name === name)
      if (!override) return base

      if(override.rules && typeof override.rules === 'string') {
        override.rules = eval(override.rules)
      }

      return { ...base, ...override }
    },

    initializeDamStartNodeId() {
      const configured = this.configuredDamStartNodeIds

      this.currentDamStartNodeId.references = this.resolveContentSelectorStartNodeId(configured.references)
      this.currentDamStartNodeId.finals = this.resolveContentSelectorStartNodeId(configured.finals)
    },

    resolveContentSelectorStartNodeId(configuredNodeId = null) {
      return this.jobData?.job_folder_id || configuredNodeId || this.damStartNodeId
    },

    toggleCommentsPanel() {
      this.isCommentsCollapsed = !this.isCommentsCollapsed
    },

    onCommentsLoaded(hasUserComments) {
      // Open the panel if there are user comments, otherwise keep it closed
      this.isCommentsCollapsed = !hasUserComments
    },
  }
}
</script>

<style scoped>
.job-modal {
  z-index: 1000;
}

.job-modal-content {
  display: flex;
  flex-direction: row;
}

.job-description-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  width: calc(100% - 33.33%);
  min-width: 0;
}

.comments-section-wrapper {
  position: relative;
  transition: all 0.3s ease;
  flex-shrink: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.comments-section-wrapper:not(.collapsed) {
  width: 33.33%;
  min-width: 300px;
}

.comments-section-wrapper.collapsed {
  width: 40px !important;
  min-width: 40px !important;
}

.comments-section-wrapper.collapsed .comments-section {
  display: none;
}

.comments-toggle-btn {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 0 4px 4px 0;
  cursor: pointer;
  padding: 8px 4px;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.comments-toggle-btn:hover {
  background: #f5f5f5;
  box-shadow: 2px 0 6px rgba(0, 0, 0, 0.15);
}

.comments-section {
  border-left: 1px solid #e0e0e0;
  width: 100%;
  height: 100%;
  flex-shrink: 0;
}

/* Position file action menu at the bottom-right of the card */
.file-card-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
}
/* Ensure inner card stretches to full column height like in Contents */
::v-deep .file-card-wrapper .v-card {
  height: 100%;
  width: 100%;
}
::v-deep .w-100 { width: 100%; }
.file-actions-bottom-right {
  position: absolute;
  right: 8px;
  bottom: 8px;
  z-index: 10000;
}

.job-header {
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
}

.job-title {
  margin: 0;
  font-weight: 500;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.job-form-content {
  overflow-y: auto;
}

.form-row {
  margin-bottom: 12px;
}

.section-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.section-title {
  font-size: 16px;
  font-weight: 500;
}

.file-links-container,
.status-container {
  min-height: 100px;
}

.file-link-section {
  text-align: center;
}

.file-placeholder,
.status-diagram-placeholder,
.comments-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100px;
  padding: 20px;
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  background-color: #fafafa;
}

.comments-card {
  height: 100%;
}

.comments-placeholder {
  height: calc(100% - 60px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.refs-panel {
  z-index: 1100;
}

.refs-empty {
  font-size: 14px;
}

/* Action bar under references title */
  .refs-actions {
    display: flex;
    align-items: center;
  }

  /* Reduce toolbar vertical padding/height in files panel */
  .files-toolbar .v-toolbar__content {
    min-height: 40px !important;
    height: 40px !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }

/* Single-line instructions in list */
.instruction-cell {
  display: inline-flex;
  align-items: center;
  width: 100%;
  cursor: pointer;
}
.instruction-text {
  display: inline-block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Ensure inline floating editor renders above job drawer and has width */

/* Compact, borderless inputs inside table rows */
.inline-input.v-input--dense .v-input__slot,
.inline-input .v-input__slot {
  min-height: 28px !important;
  height: 28px !important;
  padding: 0 6px !important;
}
.inline-input .v-text-field__slot input {
  padding: 0 !important;
  font-size: 13px !important;
}
.inline-input.no-border .v-input__slot {
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
}
.inline-input.solo.v-input .v-input__slot,
.inline-input.v-input--is-focused .v-input__slot {
  box-shadow: none !important;
}
.compact-select {
  display: inline-block;
  flex: 0 0 auto !important;
}
.cell-value {
  display: inline-block;
  min-width: 28px;
  font-size: 13px;
}

/* Responsive adjustments */
@media (max-width: 1200px) {
  .job-modal-content {
    flex-direction: column;
  }

  .comments-section {
    border-left: none;
    border-top: 1px solid #e0e0e0;
    min-height: 200px;
    width: 100% !important;
  }
}
</style>
